import React, { useState } from "react";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { FaLock } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { HiCheck } from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import NoTranslate from "../../../atoms/NoTranslate";
import { Spinner } from "../../../atoms/Spinner";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { PlansModal } from "../../../modals/PlansModal";
import { AddCreditModal } from "../entities/AddCreditModal";
import { Switch } from "../../../atoms/Switch";
import useAuth from "../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  useGetWhatsappConfig,
  useWhatsappSenderStatus,
  useRefreshWhatsappProfile,
  useSetWhatsappReplyNotification,
} from "../../../../hooks/messages";
import { useGetEntityOwnerData } from "../../../../hooks/entity";
import {
  CreditKind,
  CreditSyncResult,
  useSyncCreditPayments,
} from "../../../../hooks/billing";
import { senderStatusLabel } from "../../../../utils/whatsappSender";
import {
  hasOfflineReasons,
  offlineReasonKey,
  OfflineReason,
} from "../../../../utils/whatsappOfflineReasons";

type ProfileResult = {
  sent: boolean;
  skipped: boolean;
  skippedReason: string | null;
  error: string | null;
};

interface WhatsAppChannelSettingsProps {
  onOpenSection: (accordionId: string) => void;
}

const PLAN_REQUIRED = 402;
const BLOCK_TITLE = "text-sm font-medium text-gray-700";
const GRID = "grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5";
const TERM = "text-xs text-gray-500";
const VALUE = "text-sm text-gray-800";

const WhatsAppChannelSettings: React.FC<WhatsAppChannelSettingsProps> = ({
  onOpenSection,
}) => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);

  const k = (suffix: string) => `dashboard.settings.whatsappChannel.${suffix}`;
  const wk = (suffix: string) =>
    `dashboard.entities.viewEntityUser.sendWhatsApp.${suffix}`;

  const canSendWhatsapp = permissions.hasFeatureAccess("SEND_WHATSAPP");
  const mayUseWhatsapp = permissions.can(Cap.SEND_WHATSAPP, Level.WRITE);
  const canSendSms = permissions.hasFeatureAccess("SEND_SMS");
  const navigate = useNavigate();
  const isOwner = permissions.isOwner();

  const [creditKind, setCreditKind] = useState<"sms" | "whatsapp" | null>(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [profileResult, setProfileResult] = useState<ProfileResult | null>(
    null
  );
  const [syncResult, setSyncResult] = useState<CreditSyncResult | null>(null);

  const {
    data: config,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetWhatsappConfig(
    customer_identification,
    user.entity_identification,
    canSendWhatsapp && mayUseWhatsapp
  );

  const { data: senderStatus } = useWhatsappSenderStatus(
    customer_identification,
    user.entity_identification,
    canSendWhatsapp &&
      !!config?.sender?.from &&
      config?.sender_status !== "active"
  );

  const { data: smsCreditData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "sms_credit",
    { enabled: canSendSms }
  );

  const planLocked =
    (error as AxiosError | null)?.response?.status === PLAN_REQUIRED;

  const { mutate: refreshProfile, isLoading: isSendingProfile } =
    useRefreshWhatsappProfile();
  const { mutate: setReplyNotification, isLoading: isSavingNotification } =
    useSetWhatsappReplyNotification();
  const { mutate: syncCredits, isLoading: isSyncingCredits } =
    useSyncCreditPayments();

  const lockedView = (
    <>
      <p className="mb-3 text-sm leading-relaxed text-gray-600">
        {t(k("lockedIntro"))}
      </p>
      <ul className="mb-4 space-y-1.5">
        {[
          "lockedBenefitNumber",
          "lockedBenefitTemplates",
          "lockedBenefitPayment",
        ].map((benefit) => (
          <li key={benefit} className="flex items-start gap-2">
            <HiCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <span className="text-sm leading-tight text-gray-700">
              {t(k(benefit))}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <span className="text-sm font-medium text-gray-700">
          {t(k("lockedAction"))}
        </span>
        <UnlockBadge onClick={() => setShowPlansModal(true)} />
      </div>
      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.ENTERPRISE}
      />
    </>
  );

  if (!mayUseWhatsapp) {
    return <p className="text-sm text-gray-600">{t(k("noAccess"))}</p>;
  }

  if (!canSendWhatsapp) return lockedView;

  if (isLoading) {
    return (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );
  }

  if (isError && planLocked) return lockedView;

  if (isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-700">{t(k("configUnreadable"))}</p>
        <CustomButton
          variant="secondary"
          onClick={() => refetch()}
          title={t("common.retry") as string}
        />
      </div>
    );
  }

  const sender = config?.sender ?? null;
  const liveStatus = senderStatus?.sender_status || sender?.status || null;
  const statusLabel = senderStatusLabel(liveStatus, t);
  const whatsappCredit = config?.whatsapp_credit ?? 0;
  const smsCredit = smsCreditData?.entity_other_data?.sms_credit ?? 0;

  const profile = config?.profile ?? null;
  const logoUrl = profile?.logo_url || null;
  const description = (profile?.description || "").trim();
  const profileEmail = (profile?.emails?.[0] || "").trim();
  const publicPage = (profile?.websites?.[0] || "").trim();
  const offlineReasons: OfflineReason[] =
    liveStatus === "active"
      ? []
      : senderStatus?.offline_reasons || sender?.offline_reasons || [];

  const replyNotification = config?.reply_notification !== false;

  const toggleReplyNotification = (enabled: boolean) => {
    setReplyNotification({
      customer_identification,
      entity_identification: user.entity_identification,
      enabled,
    });
  };

  const runCreditSync = () => {
    const kinds: CreditKind[] = [
      ...(canSendSms ? (["sms"] as CreditKind[]) : []),
      ...(sender?.from ? (["whatsapp"] as CreditKind[]) : []),
    ];
    setSyncResult(null);
    syncCredits(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        kinds,
      },
      { onSuccess: (result) => setSyncResult(result) }
    );
  };

  const sendProfile = () => {
    setProfileResult(null);
    refreshProfile(
      {
        customer_identification,
        entity_identification: user.entity_identification,
      },
      {
        onSuccess: (response: any) => {
          const element = response?.data?.element ?? {};
          setProfileResult({
            sent: element.sent === true,
            skipped: element.skipped === true,
            skippedReason: element.skipped_reason || null,
            error: element.error || null,
          });
        },
      }
    );
  };

  return (
    <div className="divide-y divide-gray-100">
      <section className="space-y-2 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className={BLOCK_TITLE}>{t(k("numberTitle"))}</h3>
          {statusLabel && (
            <span className="flex items-center gap-1.5 text-xs text-gray-600">
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  liveStatus === "active"
                    ? "bg-green-500"
                    : liveStatus === "pending"
                    ? "bg-amber-500"
                    : "bg-gray-300"
                }`}
              />
              {statusLabel}
            </span>
          )}
        </div>
        {sender?.from ? (
          <dl className={GRID}>
            <dt className={TERM}>{t(k("numberTerm"))}</dt>
            <dd className={VALUE}>
              <NoTranslate>{sender.from}</NoTranslate>
            </dd>
          </dl>
        ) : (
          <p className="text-sm text-gray-500">{t(k("noSender"))}</p>
        )}

        {hasOfflineReasons(offlineReasons) && (
          <div className="mt-2 space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-medium text-amber-900">
              {t(k("offlineReason.title"))}
            </p>
            {offlineReasons.map((reason) => {
              const key = offlineReasonKey(reason.code);
              return (
                <p
                  key={`${reason.code}-${reason.message}`}
                  className="text-xs text-amber-800"
                >
                  {key ? t(key) : <NoTranslate>{reason.message}</NoTranslate>}
                </p>
              );
            })}
            <p className="text-xs text-amber-700">
              {t(k("offlineReason.contactSupport"))}
            </p>
          </div>
        )}

        {!sender?.from && (
          <p className="flex items-start gap-2 pt-1 text-xs text-gray-500">
            <FaLock className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
            <span>
              {t(wk("setupBySupportExplanation"))}{" "}
              <button
                type="button"
                onClick={() =>
                  navigate("/control-panel/support?requestType=whatsapp_setup")
                }
                className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
              >
                {t(wk("setupBySupportCta"))}
              </button>
            </span>
          </p>
        )}
      </section>

      <section className="space-y-2 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
          <div className="min-w-0 flex-1 sm:basis-64">
            <h3 className={BLOCK_TITLE}>{t(k("creditsTitle"))}</h3>
            <dl className={`${GRID} mt-2`}>
              {canSendSms && (
                <>
                  <dt className={TERM}>{t(k("smsCreditTerm"))}</dt>
                  <dd className={`${VALUE} tabular-nums`}>{smsCredit}</dd>
                </>
              )}
              {sender?.from && (
                <>
                  <dt className={TERM}>{t(k("whatsappCreditTerm"))}</dt>
                  <dd className={`${VALUE} tabular-nums`}>{whatsappCredit}</dd>
                </>
              )}
            </dl>
            {isOwner && (
              <button
                type="button"
                onClick={runCreditSync}
                disabled={isSyncingCredits}
                className="mt-2 text-xs font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 disabled:opacity-50"
              >
                {t(k(isSyncingCredits ? "syncingCredits" : "syncCredits"))}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canSendSms && (
              <CustomButton
                variant="secondary"
                onClick={() => setCreditKind("sms")}
                title={t(k("addSmsCredit")) as string}
              />
            )}
            {sender?.from && (
              <CustomButton
                variant="secondary"
                onClick={() => setCreditKind("whatsapp")}
                title={t(k("addWhatsappCredit")) as string}
              />
            )}
          </div>
        </div>
        {syncResult && (
          <div className="space-y-1">
            {syncResult.sms_added > 0 && (
              <p className="text-xs text-gray-500">
                {t(k("syncRecoveredSms"), { count: syncResult.sms_added })}
              </p>
            )}
            {syncResult.whatsapp_added > 0 && (
              <p className="text-xs text-gray-500">
                {t(k("syncRecoveredWhatsapp"), {
                  count: syncResult.whatsapp_added,
                })}
              </p>
            )}
            {syncResult.sms_added === 0 && syncResult.whatsapp_added === 0 && (
              <p className="text-xs text-gray-500">{t(k("syncNothing"))}</p>
            )}
          </div>
        )}
      </section>

      {isOwner && sender?.from && (
        <section className="py-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <label
              htmlFor="whatsapp_reply_notification"
              className="text-sm font-medium text-gray-700"
            >
              {t(k("replyNotificationLabel"))}
            </label>
            <Switch
              id="whatsapp_reply_notification"
              checked={replyNotification}
              disabled={isSavingNotification}
              onChange={toggleReplyNotification}
            />
          </div>
        </section>
      )}

      {sender?.from && (
        <section className="space-y-3 pt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
            <div className="min-w-0 flex-1 sm:basis-64">
              <h3 className={BLOCK_TITLE}>{t(k("profileTitle"))}</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                {t(k("profileReadOnly"))}{" "}
                <button
                  type="button"
                  onClick={() => onOpenSection("acc-service-information")}
                  className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                >
                  {t(k("editInServiceInfo"))}
                </button>
              </p>
            </div>
            {isOwner && (
              <button
                type="button"
                onClick={sendProfile}
                disabled={isSendingProfile}
                className="text-sm font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 disabled:opacity-50"
              >
                {t(k(isSendingProfile ? "sendingProfile" : "sendProfile"))}
              </button>
            )}
          </div>

          <dl className={GRID}>
            <dt className={TERM}>{t(k("displayNameTerm"))}</dt>
            <dd className={VALUE}>
              {sender?.display_name ? (
                <NoTranslate>{sender.display_name}</NoTranslate>
              ) : (
                <span className="text-gray-400">{t(k("notSet"))}</span>
              )}
            </dd>
            <dt className={TERM}>{t(k("logoTerm"))}</dt>
            <dd>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-10 w-10 rounded-md border border-gray-200 object-cover"
                />
              ) : (
                <span className="text-sm text-gray-400">{t(k("notSet"))}</span>
              )}
            </dd>
            <dt className={TERM}>{t(k("descriptionTerm"))}</dt>
            <dd className={VALUE}>
              {description ? (
                <NoTranslate>{description}</NoTranslate>
              ) : (
                <span className="text-gray-400">{t(k("notSet"))}</span>
              )}
            </dd>
            <dt className={TERM}>{t(k("publicPageTerm"))}</dt>
            <dd className={VALUE}>
              {publicPage ? (
                <NoTranslate>{publicPage}</NoTranslate>
              ) : (
                <span className="text-gray-400">{t(k("notSet"))}</span>
              )}
            </dd>
            <dt className={TERM}>{t(k("emailTerm"))}</dt>
            <dd className={VALUE}>
              {profileEmail ? (
                <NoTranslate>{profileEmail}</NoTranslate>
              ) : (
                <span className="text-gray-400">{t(k("notSet"))}</span>
              )}
            </dd>
          </dl>

          {profileResult?.error ? (
            <p className="text-xs text-red-600">
              <NoTranslate>{profileResult.error}</NoTranslate>
            </p>
          ) : profileResult?.skipped ? (
            <p className="text-xs text-gray-500">
              {t(
                profileResult.skippedReason === "nothing_to_send"
                  ? k("profileNothingToSend")
                  : k("profileNoSender")
              )}
            </p>
          ) : profileResult?.sent ? (
            <p className="text-xs text-gray-500">{t(k("profileSent"))}</p>
          ) : null}

          <p className="text-xs text-gray-500">{t(k("displayNameNotice"))}</p>
        </section>
      )}

      <AddCreditModal
        show={creditKind !== null}
        onClose={() => setCreditKind(null)}
        kind={creditKind ?? "whatsapp"}
      />
    </div>
  );
};

export default WhatsAppChannelSettings;
